import { UploadOutlined } from '@ant-design/icons';
import { Project } from '@common';
import { PrimaryButton, InputMask } from '@components/atoms';
import { Button, Col, Form, Input, Modal, Row, Upload, InputNumber } from 'antd';
import { useEffect, useState } from 'react';
import { UploadFile } from 'antd/es/upload/interface';
import './styles.css';

const { TextArea } = Input;

interface AddProjectModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (project: any) => void;
  initialValues?: Project;
}

export const AddProjectModal = ({ isOpen, onCancel, onSave, initialValues }: AddProjectModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setImageFile(null);

      if (initialValues) {
        const formValues = {
          ...initialValues,
          name: initialValues.name || initialValues.title,
        };
        form.setFieldsValue(formValues);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleImageUpload = (file: UploadFile) => {
    setImageFile(file as unknown as File);
    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const projectData = {
        name: values.name,
        description: values.description,
        budget: values.budget,
        siteArea: values.siteArea,
        earthMoved: values.earthMoved,
        duration: values.duration,
        completionYear: values.completionYear,
        image: imageFile,
      };

      onSave(projectData);
      form.resetFields();
      setImageFile(null);

    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = initialValues
    ? <div className="!font-['Android_101'] !font-normal">EDIT PROJECT</div>
    : <div className="!font-['Android_101'] !font-normal">ADD NEW PROJECT</div>;

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="project-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={<span className="text-white font-['Barlow']">PROJECT NAME</span>}
              rules={[{ required: true, message: 'Please enter the project name' }]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: West River Street Park" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={<span className="text-white font-['Barlow']">DESCRIPTION</span>}
            >
              <TextArea
                rows={4}
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Detailed project description"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="budget"
              label={<span className="text-white font-['Barlow']">BUDGET</span>}
            >
              <InputMask
                mask="dollar"
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="$2,500,000"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="completionYear"
              label={<span className="text-white font-['Barlow']">COMPLETION YEAR</span>}
            >
              <InputNumber 
                min={1900}
                max={2100}
                className="w-full bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Ex: 2024"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="siteArea"
              label={<span className="text-white font-['Barlow']">SITE AREA</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: 15 acres" 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="earthMoved"
              label={<span className="text-white font-['Barlow']">EARTH MOVED</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: 25,000 cubic yards" 
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="duration"
              label={<span className="text-white font-['Barlow']">DURATION</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: 8 months" 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="image"
              label={<span className="text-white font-['Barlow']">PROJECT IMAGE</span>}
            >
              <Upload
                name="image"
                listType="text"
                accept="image/*"
                beforeUpload={handleImageUpload}
                maxCount={1}
                showUploadList={{
                  showRemoveIcon: true,
                }}
              >
                <Button 
                  className="!border-gray-700"
                  icon={<UploadOutlined />}
                >
                  <span className="font-android !font-normal text-white">CHOOSE IMAGE</span>
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} justify="end">
          <Col>
            <PrimaryButton
              onClick={onCancel}
              className="mr-2 !text-white !font-['Barlow']"
            >
              CANCEL
            </PrimaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              loading={loading}
              className="!text-black !font-['Barlow']"
            >
              {initialValues ? 'UPDATE' : 'SAVE'}
            </PrimaryButton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}; 